import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { IconCheck } from "@tabler/icons-react";
import { forwardRef, useId, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export interface CheckboxProps
  extends Omit<React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>, "asChild"> {
  label?: ReactNode;
  description?: ReactNode;
}

/** Reemplazo de `<Checkbox>` de Mantine (Radix UI). */
export const Checkbox = forwardRef<React.ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(
  ({ className, label, description, id, ...props }, ref) => {
    // Id estable de respaldo · sin `id`/`name`, el `htmlFor` del label quedaba
    // undefined y el checkbox de Radix se quedaba SIN nombre accesible (a11y).
    const reactId = useId();
    const autoId = id ?? props.name ?? reactId;
    const labelId = `${autoId}-label`;
    // El checkbox de Radix es un `<button role="checkbox">` · un `<label for>` NO
    // le da nombre accesible ahí, así que lo enlazamos por `aria-labelledby` al
    // texto visible del label (salvo que el caller ya pase aria-label/labelledby).
    const { "aria-label": ariaLabel, "aria-labelledby": ariaLabelledby, ...rest } = props;
    const box = (
      <CheckboxPrimitive.Root
        ref={ref}
        id={autoId}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby ?? (label ? labelId : undefined)}
        className={cn(
          "flex size-4 shrink-0 items-center justify-center rounded-sm border border-input bg-background transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-multiva-600/30",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          "data-[state=checked]:bg-multiva-600 data-[state=checked]:border-multiva-600",
          className
        )}
        {...rest}
      >
        <CheckboxPrimitive.Indicator className="text-white">
          <IconCheck size={11} strokeWidth={3} />
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
    );
    if (!label && !description) return box;
    return (
      <div className="flex items-start gap-2">
        {box}
        <div className="flex flex-col gap-0.5">
          {label && (
            <label id={labelId} htmlFor={autoId} className="cursor-pointer text-sm text-foreground">
              {label}
            </label>
          )}
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
      </div>
    );
  }
);
Checkbox.displayName = "Checkbox";
